import type { BuiltInTool, BuiltInToolError } from './types'

const NAME_ERRORS: readonly BuiltInToolError[] = [
    { code: 'name_required', when: '`name` is missing, or blank once whitespace is trimmed.', handling: 'tool_error' },
    { code: 'name_too_long:200', when: '`name` is over 200 characters.', handling: 'tool_error' },
]

/** Tools a task uses to label its execution: the connected runs of several tasks, shown as one on Executions. */
export const OS_EXECUTION_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'os.set_execution_name',
        family: 'os.execution',
        summary: "Replace the execution's default title with a human-readable name.",
        description:
            'Set a human-readable name for this execution, shown on Executions and run detail instead of the first task name. Later calls replace it. Pass name, e.g. "GitHub Issue #105 - Work keeps moving while decisions wait on the CEO".',
        inputSchema: {
            type: 'object',
            properties: {
                name: { type: 'string', description: 'Shown on Executions instead of the first task name' },
            },
            required: ['name'],
            additionalProperties: false,
        },
        returns: 'The name as stored: whitespace collapsed and trimmed.',
        callers: ['run', 'api'],
        effect: 'write',
        retry: 'idempotent',
        retryNote: 'The last successful call in the execution wins, so a repeat leaves the same name.',
        errors: NAME_ERRORS,
        api: { method: 'POST', path: '/v1/runs/self/execution-name', scope: 'run:self' },
        examples: [{ input: { name: 'Issue #12 - Export the monthly report as CSV' } }],
        public: true,
    },
    {
        name: 'os.add_execution_link',
        family: 'os.execution',
        summary: 'Attach an http(s) link to the execution: an issue, a pull request, a preview.',
        description:
            'Attach a helpful http(s) link to this execution (issue, PR, preview, CRM profile). Shown on Executions and run detail. The same URL later updates the label. Pass name and url.',
        inputSchema: {
            type: 'object',
            properties: {
                name: { type: 'string', description: 'Label, e.g. GitHub issue, Pull request, Preview' },
                url: { type: 'string', description: 'http(s) URL' },
            },
            required: ['name', 'url'],
            additionalProperties: false,
        },
        callers: ['run', 'api'],
        effect: 'write',
        retry: 'idempotent',
        retryNote: 'The same URL updates its label rather than adding a second link.',
        errors: [
            ...NAME_ERRORS,
            { code: 'url_required', when: '`url` is missing or blank.', handling: 'tool_error' },
            { code: 'url_too_long:2000', when: '`url` is over 2000 characters.', handling: 'tool_error' },
            { code: 'url_invalid', when: '`url` is not an `http:` or `https:` URL.', handling: 'tool_error' },
        ],
        api: { method: 'POST', path: '/v1/runs/self/execution-links', scope: 'run:self' },
        examples: [{ input: { name: 'Pull request', url: 'https://git.example.com/acme/app/pull/34' } }],
        public: true,
    },
]
